import React, { useState } from 'react'
import router from '@Site/Router'
import capitalize from 'lodash/capitalize'
import Button from '@SiteComponents/Button'
import Popup from '@SiteComponents/Popup'

import './SetupWarningPopup.scss'

const WARNING_RANKINGS = [
  {
    type: 'unsupported_dubbing',
    ranking: 1,
  }, {
    type: 'unsupported_voice_cloning_source_language',
    ranking: 2,
  }, {
    type: 'unsupported_voice_cloning_translation_language',
    ranking: 3,
  }, {
    type: 'unsupported_script_upload',
    ranking: 4,
  }, {
    type: 'duration_zero',
    ranking: 5,
  }, {
    type: 'missing_assets',
    ranking: 6,
  }, {
    type: 'subscription_not_current',
    ranking: 7,
  }, {
    type: 'features_unsupported_by_subscription',
    ranking: 8,
  }, {
    type: 'too_many_languages_for_subscription',
    ranking: 9,
  }, {
    type: 'not_enough_total_and_analysis_credits',
    ranking: 10
  }, {
    type: 'not_enough_total_credits',
    ranking: 11
  }, {
    type: 'not_enough_analysis_credits',
    ranking: 12
  }
]

export interface IWarning {
  readonly type: string,
  readonly info?: string[]
}

export interface IWarningDetail {
  readonly header: string,
  readonly body: JSX.Element,
  readonly primaryButtonCaption: string,
  readonly secondaryButtonCaption: string
  readonly resetAsWell?: string[]
  primaryAction(): void
  secondaryAction(): void
}

interface SetupWarningPopupProps {
  readonly warningsToShow: IWarning[]
  readonly projectVersionLanguage: string
  readonly hasNewTranscription: boolean
  readonly userIsAccountAdmin: boolean
  readonly adminEmails: string[]
  readonly currentAccountSubscriptionMaxLanguagesLimit: number
  readonly totalNumberOfSubscriptionSubtitles: number
  readonly isNonExistingProject: boolean
  readonly newTranslationLanguages: string[]
  readonly shouldNotRenderOverlay?: boolean
  handleClose(): void
  submit(): void
  resetWarnings(): void
  updateIgnoredWarningsAndTrySubmit(warningsToIgnore: string[]): void
  handleCloseAndKeepEverything(): void
}

const SetupWarningPopup: React.FC<SetupWarningPopupProps> = props => {
  const {
    warningsToShow,
    projectVersionLanguage,
    hasNewTranscription,
    userIsAccountAdmin,
    adminEmails,
    currentAccountSubscriptionMaxLanguagesLimit,
    totalNumberOfSubscriptionSubtitles,
    isNonExistingProject,
    newTranslationLanguages,
    shouldNotRenderOverlay,
  } = props

  const [hasTriedToContactSupport, setHasTriedToContactSupport] = useState(false)

  const currentWarning = warningsToShow.reduce((lowest, current) => {
    const currentRanking = WARNING_RANKINGS.find(r => r.type === current.type)?.ranking ?? Infinity
    const lowestRanking = WARNING_RANKINGS.find(r => r.type === lowest.type)?.ranking ?? Infinity

    return currentRanking < lowestRanking ? current : lowest
  }, warningsToShow[0])

  const formatInfo = (info: string[] | undefined) => {
    if (!info || info.length === 0) {
      return ''
    }

    if (info.length === 1) {
      return info[0]
    }

    if (info.length === 2) {
      return `${info[0]} and ${info[1]}`
    }

    return `${info.slice(0, -1).join(', ')} and ${info[info.length - 1]}`
  }

  const handleSubmit = () => {
    const warningsToIgnore = currentWarningDetails && currentWarningDetails.resetAsWell ? [currentWarning.type].concat(currentWarningDetails.resetAsWell) : [currentWarning.type]
    props.updateIgnoredWarningsAndTrySubmit(warningsToIgnore)
  }

  const handleContactSupport = () => {
    setHasTriedToContactSupport(true)

    const projectVersionIds = currentWarning.info!.join(', ')
    const subject = encodeURIComponent('Unable to process files - Element duration issues')
    const body = encodeURIComponent(
      `      Hello Checksub Support Team,

      I'm experiencing an issue with file processing in my projects.

      Project Version IDs: ${projectVersionIds}

      Error Details:
      - Duration of 0 detected

      Please help me resolve this issue.

      Thank you.`
    )

    const mailToLink = `mailto:team+support@checksub.com?subject=${subject}&body=${body}`
    window.location.href = mailToLink
  }

  const handleContactAdminRenew = () => {
    const subject = encodeURIComponent('Renew Checksub License Request')
    const body = encodeURIComponent(
      `      Hello,

      I would like to request a renewal of our Checksub license.

      Thank you.`
    )

    const mailToLink = `mailto:${adminEmails.join(',')}?subject=${subject}&body=${body}`
    window.location.href = mailToLink
  }

  const handleContactAdminUpgrade = () => {
    const subject = encodeURIComponent('Upgrade Checksub License Request')
    const body = encodeURIComponent(
      `      Hello,

      I would like to request an upgrade of our Checksub license to have more credits, languages and to access advanced features such as voice-cloning.

      Thank you.`
    )

    const mailToLink = `mailto:${adminEmails.join(',')}?subject=${subject}&body=${body}`
    window.location.href = mailToLink
  }

  const goToSubscriptionPage = () => {
    router.navigate('/subscriptions')
  }

  let currentWarningDetails: null | IWarningDetail = null

  let bodyLastPartVoice = 'You can either create the project with subtitles only or you can abort the setup.'
  let secondaryActionVoice = props.handleClose
  let secondaryButtonCaptionVoice = 'Abort'
  const numberOfNewTranslationLanguages = newTranslationLanguages.length
  const numberOfLanguagesNotSupportingVoice = currentWarning.info ? currentWarning.info.length : 0

  if (currentWarning.type === 'unsupported_voice_cloning_translation_language' && currentWarning.info) {
    if (numberOfLanguagesNotSupportingVoice < numberOfNewTranslationLanguages) {
      secondaryActionVoice = props.resetWarnings
      secondaryButtonCaptionVoice = 'Go back and edit'

      if (numberOfLanguagesNotSupportingVoice > 1) {
        bodyLastPartVoice = 'You can either remove these languages and continue with voice-cloning for the others, or keep them and generate subtitles only for all languages selected.'

      } else if (numberOfLanguagesNotSupportingVoice === 1) {
        bodyLastPartVoice = 'You can either remove this language and continue with voice-cloning for the others, or keep it and generate subtitles only for all languages selected.'
      }
    }
  } else if (currentWarning.type === 'unsupported_dubbing' && currentWarning.info) {
    if (numberOfLanguagesNotSupportingVoice < numberOfNewTranslationLanguages) {
      secondaryActionVoice = props.resetWarnings
      secondaryButtonCaptionVoice = 'Go back and edit'

      if (numberOfLanguagesNotSupportingVoice > 1) {
        bodyLastPartVoice = 'You can either remove these languages and generate the dubbing for the others, or keep them and generate subtitles only for all languages selected.'

      } else if (numberOfLanguagesNotSupportingVoice === 1) {
        bodyLastPartVoice = 'You can either remove this language and generate the dubbing for the others, or keep it and generate subtitles only for all languages selected.'
      }
    }
  }

  switch(currentWarning.type) {
    case 'unsupported_dubbing':
      currentWarningDetails = {
        header: `Some languages don't support dubbing`,
        body: (
          <div className="SetupWarningPopup__body">
            <div className="SetupWarningPopup__body-first-part">
                Dubbing isn't available for
              <span>
              {' '}
              </span>
              <span className="SetupWarningPopup__body-highlighted-part">
                {formatInfo(currentWarning.info)}
              </span>
              .
            </div>
            <div className="SetupWarningPopup__body-last-part">
              {bodyLastPartVoice}
            </div>
          </div>
        ),
        primaryAction: handleSubmit,
        secondaryAction: secondaryActionVoice,
        primaryButtonCaption: 'Yes, create project',
        secondaryButtonCaption: secondaryButtonCaptionVoice,
        resetAsWell: ['unsupported_voice_cloning_source_language', 'unsupported_voice_cloning_translation_language']
      }
      break
    case 'unsupported_voice_cloning_source_language':
      currentWarningDetails = {
        header: 'Voice-cloning unavailable',
        body: (
          <div className="SetupWarningPopup__body">
            <div className="SetupWarningPopup__body-first-part">
              Unfortunately, the source language
              <span>
                {' '}
              </span>
              <span className="SetupWarningPopup__body-highlighted-part">
                {projectVersionLanguage}
              </span>
              <span>
                {' '}
              </span>
              does not support voice-cloning.<br />
            </div>
            <div className="SetupWarningPopup__body-last-part">
              You can still continue with your project, but no dubbing will be generated.
            </div>
          </div>
        ),
        primaryAction: handleSubmit,
        secondaryAction: hasNewTranscription ? props.resetWarnings : props.handleClose,
        primaryButtonCaption: 'Yes, create project',
        secondaryButtonCaption: hasNewTranscription ? 'Go back and edit' : 'Cancel',
        resetAsWell: ['unsupported_voice_cloning_translation_language']
      }
      break
    case 'unsupported_voice_cloning_translation_language':
      currentWarningDetails = {
        header: 'Voice-cloning unavailable',
        body: (
          <div className="SetupWarningPopup__body">
            <div className="SetupWarningPopup__body-first-part">
                Voice-cloning isn't available for
              <span>
              {' '}
              </span>
              <span className="SetupWarningPopup__body-highlighted-part">
                {formatInfo(currentWarning.info)}
              </span>
              .
            </div>
            <div className="SetupWarningPopup__body-last-part">
              {bodyLastPartVoice}
            </div>
          </div>
        ),
        primaryAction: handleSubmit,
        secondaryAction: secondaryActionVoice,
        primaryButtonCaption: 'Yes, create project',
        secondaryButtonCaption: secondaryButtonCaptionVoice
      }
      break
    case 'unsupported_script_upload':
      currentWarningDetails = {
        header: 'Script upload not supported',
        body: (
          <div className="SetupWarningPopup__body">
            <div className="SetupWarningPopup__body-first-part">
              Unfortunately, the selected language
              <span>
                {' '}
              </span>
              <span className="SetupWarningPopup__body-highlighted-part">
                {projectVersionLanguage}
              </span>
              <span>
                {' '}
              </span>
              does not support script alignment.
            </div>
            <div className="SetupWarningPopup__body-last-part">
              You can still continue with your project, but the uploaded script will not be used for automatic alignment.
            </div>
          </div>
        ),
        primaryAction: handleSubmit,
        secondaryAction: props.resetWarnings,
        primaryButtonCaption: 'Yes, create project',
        secondaryButtonCaption: 'Go back and edit',
      }
      break
    case 'duration_zero':
      currentWarningDetails = {
        header: 'Unable to process your file',
        body: (
          <div className="SetupWarningPopup__body">
            <div className="SetupWarningPopup__body-first-part">
              We're having trouble reading your video/audio file. <br />
              This could be due to file corruption or an unsupported format.
            </div>
            <div className="SetupWarningPopup__body-last-part">
              Our support team can help you resolve this issue and get your project back on track. Please reach out to us with details about your file.
            </div>
          </div>
        ),
        primaryAction: handleContactSupport,
        secondaryAction: isNonExistingProject ? props.resetWarnings : props.handleClose,
        primaryButtonCaption: 'Contact support',
        secondaryButtonCaption: isNonExistingProject ? 'Try another file' : 'Cancel'
      }
      break
    case 'subscription_not_current':
      currentWarningDetails = {
        header: 'Subscription expired',
        body: userIsAccountAdmin ? (
          <div className="SetupWarningPopup__body">
            Your subscription has expired. <br />
            Renew your subscription to continue using all features without interruption.
          </div>
        ) : (
          <div className="SetupWarningPopup__body">
            Your team's subscription has expired. <br />
            Contact your admin to renew the subscription.
          </div>
        ),
        primaryAction: userIsAccountAdmin ? goToSubscriptionPage : handleContactAdminRenew,
        secondaryAction: props.handleClose,
        primaryButtonCaption: userIsAccountAdmin ? 'Renew subscription' : 'Contact admin',
        secondaryButtonCaption: 'Cancel',
      }
      break
    case 'features_unsupported_by_subscription':
      currentWarningDetails = {
        header: `${capitalize(formatInfo(currentWarning.info))} not available in current plan`,
        body: userIsAccountAdmin ? (
          <div className="SetupWarningPopup__body">
            {`${currentWarning.info!.length === 1 ? 'This feature requires ' : 'These features require '} a higher subscription tier.`} <br />
            Upgrade your plan to unlock advanced dubbing capabilities and premium tools.
          </div>
        ) : (
          <div className="SetupWarningPopup__body">
            {`${currentWarning.info!.length === 1 ? 'This feature is ' : 'These features are '} not included in your current plan.`} <br />
            Ask your admin about upgrading to access premium features.
          </div>
        ),
        primaryAction: userIsAccountAdmin ? goToSubscriptionPage : handleContactAdminUpgrade,
        secondaryAction: props.resetWarnings,
        primaryButtonCaption: userIsAccountAdmin ? 'Upgrade plan' : 'Contact admin',
        secondaryButtonCaption: 'Go back and edit',
      }
      break
    case 'too_many_languages_for_subscription':
      currentWarningDetails = {
        header: 'Upgrade to add more languages',
        body: userIsAccountAdmin ? (
          <div className="SetupWarningPopup__body">
            <div className="SetupWarningPopup__body-first-part">
              {`You're trying to add ${totalNumberOfSubscriptionSubtitles} languages, but your current plan only includes ${currentAccountSubscriptionMaxLanguagesLimit} languages.`}
            </div>
            <div className="SetupWarningPopup__body-last-part">
              To add more languages to your project, you'll need to upgrade your subscription. <br />
              Upgrading will give you access to additional languages and unlock more advanced features to enhance your localize projects.
            </div>
          </div>
        ) : (
          <div className="SetupWarningPopup__body">
            <div className="SetupWarningPopup__body-first-part">
              {`You're trying to add ${totalNumberOfSubscriptionSubtitles} languages, but your current plan only includes ${currentAccountSubscriptionMaxLanguagesLimit} languages.`}
            </div>
            <div className="SetupWarningPopup__body-last-part">
              To add more languages to your project, you'll need to upgrade your subscription. Contact your admin to do so.
            </div>
          </div>
        ),
        primaryAction: userIsAccountAdmin ? goToSubscriptionPage : handleContactAdminUpgrade,
        secondaryAction: props.resetWarnings,
        primaryButtonCaption: userIsAccountAdmin ? 'Upgrade plan' : 'Contact admin',
        secondaryButtonCaption: 'Go back and edit',
      }
      break
    case 'not_enough_total_and_analysis_credits':
      currentWarningDetails = {
        header: 'Insufficient balance for analysis and translation',
        body: userIsAccountAdmin ? (
          <div className="SetupWarningPopup__body">
            You don't have enough credits to process this project. <br />
            Upgrade your plan for more monthly credits.
          </div>
        ) : (
          <div className="SetupWarningPopup__body">
            Your team doesn't have enough credits for this project. <br />
            Contact your admin to upgrade the plan.
          </div>
        ),
        primaryAction: userIsAccountAdmin ? goToSubscriptionPage : handleContactAdminUpgrade,
        secondaryAction: props.resetWarnings,
        primaryButtonCaption: userIsAccountAdmin ? 'Upgrade plan' : 'Contact admin',
        secondaryButtonCaption: 'Go back',
      }
      break
    case 'not_enough_total_credits':
      currentWarningDetails = {
        header: 'Insufficient credits',
        body: userIsAccountAdmin ? (
          <div className="SetupWarningPopup__body">
            You don't have enough credits to process this project. <br />
            {`Balance: ${capitalize(formatInfo(currentWarning.info))}`} <br />
            Upgrade your plan for more monthly credits.
          </div>
        ) : (
          <div className="SetupWarningPopup__body">
            Your team doesn't have enough credits for this project. <br />
            {`Balance: ${capitalize(formatInfo(currentWarning.info))}`} <br />
            Contact your admin to upgrade the plan.
          </div>
        ),
        primaryAction: userIsAccountAdmin ? goToSubscriptionPage : handleContactAdminUpgrade,
        secondaryAction: props.resetWarnings,
        primaryButtonCaption: userIsAccountAdmin ? 'Upgrade plan' : 'Contact admin',
        secondaryButtonCaption: 'Go back and edit',
      }
      break
    case 'not_enough_analysis_credits':
      currentWarningDetails = {
        header: 'Your video is too long for your analysis plan',
        body: userIsAccountAdmin ? (
          <div className="SetupWarningPopup__body">
            You don't have enough analysis credits to process this project. <br />
            {`Balance: ${capitalize(formatInfo(currentWarning.info))}`} <br />
            Upgrade your plan for more monthly analysis credits.
          </div>
        ) : (
          <div className="SetupWarningPopup__body">
            Your team doesn't have enough analysis credits for this project. <br />
            {`Balance: ${capitalize(formatInfo(currentWarning.info))}`} <br />
            Contact your admin to upgrade the plan.
          </div>
        ),
        primaryAction: userIsAccountAdmin ? goToSubscriptionPage : handleContactAdminUpgrade,
        secondaryAction: props.resetWarnings,
        primaryButtonCaption: userIsAccountAdmin ? 'Upgrade plan' : 'Contact admin',
        secondaryButtonCaption: 'Go back',
      }
      break
  }

  const renderButtons = () => {
    return (
      <div className="SetupWarningPopup__buttons">
        <Button
          caption={currentWarningDetails!.secondaryButtonCaption}
          onClick={currentWarningDetails!.secondaryAction}
          styleType="gray-subtle"
          size="large"
        />
        <Button
          caption={currentWarningDetails!.primaryButtonCaption}
          onClick={currentWarningDetails!.primaryAction}
          styleType="brand"
          size="large"
        />
      </div>
    )
  }

  return (
    <Popup
      className="SetupWarningPopup"
      shouldNotRenderOverlay={shouldNotRenderOverlay}
      zIndex={200000}
      closePopup={hasTriedToContactSupport ? props.handleCloseAndKeepEverything : props.handleClose}
    >
      <div className="SetupProjectPopup__header">
        {currentWarningDetails!.header}
      </div>
      {currentWarningDetails!.body}
      {renderButtons()}
    </Popup>
  )
}

export default SetupWarningPopup
